import { useState } from "react";
import { http, apiError } from "@/lib/api";
import { ThumbsUp, CaretDown, CaretRight, ChatCircleText, FloppyDisk, Trash, CheckCircle } from "@phosphor-icons/react";

/**
 * SessionFeedback — inline expandable row for a client's workout log with
 * coach kudos toggle + comment textarea. Fires a push to the client on save.
 */
export default function SessionFeedback({ log, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [kudos, setKudos] = useState(!!log.coach_kudos);
  const [comment, setComment] = useState(log.coach_comment || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // Initialize from persisted feedback so the button starts as "Feedback sent"
  // if this log already has coach_comment or coach_kudos in the DB (from a
  // prior save, page reload, or reopen). Only null if no feedback yet.
  const [sentSnapshot, setSentSnapshot] = useState(
    (log.coach_comment || log.coach_kudos)
      ? { comment: log.coach_comment || "", kudos: !!log.coach_kudos }
      : null
  );
  const [deleting, setDeleting] = useState(false);

  const hasFeedback = !!log.coach_comment || !!log.coach_kudos;
  // Button label = "Feedback sent" only when the current field values still
  // match the last successful save (i.e. coach hasn't edited since).
  const isSentState = sentSnapshot && sentSnapshot.comment === comment && sentSnapshot.kudos === kudos;

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      await http.patch(`/coach/workout-logs/${log.id}`, {
        coach_comment: comment,
        coach_kudos: kudos,
      });
      setSentSnapshot({ comment, kudos });
    } catch (e) { setErr(apiError(e, "Save failed.")); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this session log? This will remove it from the client's history — use only for accidental duplicate submits.")) return;
    setErr(""); setDeleting(true);
    try {
      await http.delete(`/coach/workout-logs/${log.id}`);
      if (onDeleted) onDeleted(log.id);
    } catch (e) { setErr(apiError(e, "Delete failed.")); setDeleting(false); }
  };

  return (
    <li className="py-3" data-testid={`session-row-${log.id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`session-toggle-${log.id}`}
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={14} weight="bold" className="text-zinc-500" /> : <CaretRight size={14} weight="bold" className="text-zinc-500" />}
          <div>
            <div className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
              {log.day_name}
              {typeof log.week_number === "number" && log.week_number > 0 && (
                <span className="text-[10px] bg-[#262626] text-zinc-300 border border-[#262626] px-1.5 py-0.5" data-testid={`session-week-${log.id}`}>
                  Wk {log.week_number}
                </span>
              )}
              {hasFeedback && (
                <span className="text-[10px] bg-[#FF3333]/15 text-[#FF3333] border border-[#FF3333]/40 px-1.5 py-0.5 flex items-center gap-1">
                  {log.coach_kudos && <ThumbsUp size={10} weight="fill" />} Coach feedback
                </span>
              )}
            </div>
            <div className="text-xs text-zinc-500 font-manrope">
              {log.exercises.length} exercises · {new Date(log.logged_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      </button>

      {open && (
        <div className="mt-3 space-y-3 border-l-2 border-[#262626] pl-4" data-testid={`session-detail-${log.id}`}>
          {/* Exercise summary */}
          <ul className="text-xs font-manrope space-y-1.5">
            {log.exercises.map((ex, i) => (
              <li key={i}>
                <div>
                  <span className="text-white font-semibold">{ex.name}: </span>
                  <span className="text-zinc-400">
                    {(ex.sets || []).filter((s) => Number(s.reps) > 0 || Number(s.weight) > 0)
                      .map((s) => `${s.reps}×${s.weight}kg`).join(" · ") || "no sets logged"}
                  </span>
                </div>
                {ex.notes && (
                  <div className="text-[11px] text-zinc-500 mt-0.5 pl-2 border-l-2 border-[#262626] italic">
                    {ex.notes}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {log.notes && (
            <div className="text-xs font-manrope text-zinc-400 border-l-2 border-zinc-700 pl-2 py-1">
              <span className="text-zinc-500 uppercase tracking-[0.1em] text-[10px]">Client notes: </span>
              {log.notes}
            </div>
          )}

          {/* Coach feedback composer */}
          <div className="border border-[#262626] p-3 bg-[#0F0F0F]">
            <div className="flex items-center justify-between mb-2">
              <span className="id-label">— Coach feedback —</span>
              <label className="flex items-center gap-2 cursor-pointer text-xs font-manrope">
                <input
                  type="checkbox"
                  checked={kudos}
                  onChange={(e) => setKudos(e.target.checked)}
                  className="accent-[#FF3333]"
                  data-testid={`kudos-toggle-${log.id}`}
                />
                <span className={kudos ? "text-[#FF3333] font-semibold" : "text-zinc-400"}>
                  <ThumbsUp size={12} weight={kudos ? "fill" : "bold"} className="inline mr-1" />
                  Kudos
                </span>
              </label>
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1000}
              rows={2}
              className="id-input text-sm"
              placeholder="e.g. Solid session mate — beat last week on the squat by 5kg. Push for +2 reps next time."
              data-testid={`comment-input-${log.id}`}
            />
            {err && <div className="mt-2 text-xs text-[#FF3333]">{err}</div>}
            {isSentState && <div className="mt-2 text-xs text-[#00CC66] flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Client got a push notification. Edit the note to send an update.</div>}
            <div className="flex justify-between items-center mt-2 gap-3">
              <button
                onClick={remove}
                disabled={deleting || saving}
                className="text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500 hover:text-[#FF3333] disabled:opacity-40 flex items-center gap-1"
                data-testid={`delete-session-${log.id}`}
                title="Delete this session log (use for accidental duplicates)"
              >
                <Trash size={11} weight="bold" /> {deleting ? "Deleting…" : "Delete session"}
              </button>
              <button
                onClick={save}
                disabled={saving || deleting || isSentState}
                className={
                  "text-xs disabled:opacity-100 " +
                  (isSentState
                    ? "id-btn-ghost border-[#00CC66]/50 text-[#00CC66] cursor-default"
                    : "id-btn-primary disabled:opacity-40")
                }
                data-testid={`save-feedback-${log.id}`}
              >
                {isSentState ? (
                  <><CheckCircle size={12} weight="fill" /> Feedback sent</>
                ) : (
                  <><FloppyDisk size={12} weight="bold" /> {saving ? "Sending…" : "Send feedback"}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}
